import { useEffect, useState, useMemo, useCallback } from 'react';
import { Stack, Box } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getDigitalBalanceFeatureState, setAddBalanceState } from 'state/reducers/balance.reducers';
import { selectTranslations, selectLanguage } from 'state/reducers/translation.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';

import { getActivitiesAsync } from 'services/activity.services';
import {
	getBankAccountsByCompanyId,
	getDigitalDepositBankAccountsAvailabilitySummary,
} from 'services/bank-account.services';
import {
	getBalancesByCompanyId,
	hasRecentDigitalBalanceAdditions,
	isDigitalDepositAllowed,
} from 'services/balance.services';

import { User } from 'constants/user.constants';
import { BalanceDepositType, DigitalBalanceFeatureState } from 'constants/balance.constants';
import { FeatureFlags } from 'constants/feature-flags.constants';

import BalanceHeader from 'sections/balance/BalanceHeader';
import BalanceDashboardCard from './BalanceDashboardCard';
import WaitingMessage from 'components/WaitingMessage';
import FeatureFlagsWrapper from 'components/FeatureFlagsWrapper';
import ImagePopup from 'components/ImagePopup';
import { getAllSystemSettings } from 'services/system_settings.services';

const MANUAL_BALANCE_ALERT_IMAGE_PATH = '/static_images/digital-balance-addition-alert-icon.png';
const DIGITAL_DEPOSIT_BANK_AVAILABILITY_ALERT_IMAGE_PATH =
	'/static_images/digital-deposit-bank-availability-alert-icon.svg';

const COMPONENT_CONFIG = {
	digitalCard: {
		imagePath: '/static_images/feezback-img.png',
		depositType: BalanceDepositType.Digital,
		translationKeys: {
			title: 'page.balance.add.digital.profile.digital_deposit_card.title.text',
			description: 'page.balance.add.digital.profile.digital_deposit_card.title.description',
			buttonActive: 'page.balance.add.digital.profile.buttons.continue_when_feature_is_active.text',
			buttonInactive: 'page.balance.add.digital.profile.buttons.continue_when_feature_is_not_active.text',
		},
	},
	manualCard: {
		imagePath: '/static_images/manual-balance-img.png',
		depositType: BalanceDepositType.Manual,
		translationKeys: {
			title: 'page.balance.add.digital.profile.manual_deposit_card.title.text',
			description: 'page.balance.add.digital.profile.manual_deposit_card.title.description',
			button: 'page.balance.add.digital.profile.buttons.continue_when_feature_is_active.text',
		},
	},
} as const;

// hook to load the dashboard data
const useDashboardLoader = (companyId?: number | string) => {
	const [isLoading, setIsLoading] = useState(true);
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!companyId) return;

		const loadData = async () => {
			setIsLoading(true);
			try {
				const numericCompanyId = Number(companyId);
				const yesterday = new Date();
				yesterday.setDate(yesterday.getDate() - 1);

				await Promise.all([
					dispatch(getBalancesByCompanyId()),
					dispatch(getBankAccountsByCompanyId()),
					dispatch(getActivitiesAsync(numericCompanyId, yesterday, new Date())),
					dispatch(getAllSystemSettings()),
				]);
				await dispatch(isDigitalDepositAllowed());
			} finally {
				setIsLoading(false);
			}
		};

		loadData();
	}, [companyId, dispatch]);

	return { isLoading };
};

export default function AddBalanceDashboard() {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const lang = useAppSelector(selectLanguage);
	const currentUser: User = useAppSelector(getUserDetails);
	const digitalBalanceFeatureState = useAppSelector(getDigitalBalanceFeatureState);

	const [showManualBalanceAlert, setShowManualBalanceAlert] = useState(false);
	const [isCheckingRecentDigitalBalanceAdditions, setIsCheckingRecentDigitalBalanceAdditions] = useState(false);
	const [showDigitalDepositBankAvailabilityAlert, setShowDigitalDepositBankAvailabilityAlert] = useState(false);
	const [temporarilyUnavailableDigitalDepositBankNames, setTemporarilyUnavailableDigitalDepositBankNames] = useState<
		string[]
	>([]);

	const { isLoading } = useDashboardLoader(currentUser?.companyId);

	const isDigitalDepositDisabled = useMemo(
		() => digitalBalanceFeatureState !== DigitalBalanceFeatureState.Enabled,
		[digitalBalanceFeatureState],
	);

	const digitalBalanceValidationMessage = useMemo(() => {
		if (digitalBalanceFeatureState === DigitalBalanceFeatureState.Enabled) {
			return null;
		}
		return t(`page.balance.add.digital.profile.validation_messages.${digitalBalanceFeatureState}.text`);
	}, [digitalBalanceFeatureState, t]);

	const handleCardClick = useCallback(
		async (depositType: BalanceDepositType) => {
			if (depositType === BalanceDepositType.Digital) {
				const availabilitySummary = getDigitalDepositBankAccountsAvailabilitySummary();

				if (
					availabilitySummary.availableDigitalDepositBankAccounts.length > 0 &&
					availabilitySummary.temporarilyUnavailableDigitalDepositBankNames.length > 0
				) {
					setTemporarilyUnavailableDigitalDepositBankNames(
						availabilitySummary.temporarilyUnavailableDigitalDepositBankNames,
					);
					setShowDigitalDepositBankAvailabilityAlert(true);
					return;
				}

				dispatch(setAddBalanceState(depositType));
				return;
			}

			setIsCheckingRecentDigitalBalanceAdditions(true);
			try {
				const hasRecent = await hasRecentDigitalBalanceAdditions();
				if (hasRecent) {
					setShowManualBalanceAlert(true);
				} else {
					dispatch(setAddBalanceState(depositType));
				}
			} catch {
				dispatch(setAddBalanceState(depositType));
			} finally {
				setIsCheckingRecentDigitalBalanceAdditions(false);
			}
		},
		[dispatch],
	);

	const digitalCardProps = useMemo(() => {
		const { digitalCard } = COMPONENT_CONFIG;
		const buttonKey = isDigitalDepositDisabled
			? digitalCard.translationKeys.buttonInactive
			: digitalCard.translationKeys.buttonActive;

		return {
			onActionClick: () => handleCardClick(digitalCard.depositType),
			title: t(digitalCard.translationKeys.title),
			description: t(digitalCard.translationKeys.description),
			buttonText: t(buttonKey),
			imagePath: digitalCard.imagePath,
			balanceDepositType: digitalCard.depositType,
			digitalBalanceDisabledText: digitalBalanceValidationMessage,
			disabled: isDigitalDepositDisabled,
			lang,
		};
	}, [t, lang, isDigitalDepositDisabled, digitalBalanceValidationMessage, handleCardClick]);

	const manualCardProps = useMemo(() => {
		const { manualCard } = COMPONENT_CONFIG;

		return {
			onActionClick: () => handleCardClick(manualCard.depositType),
			title: t(manualCard.translationKeys.title),
			description: t(manualCard.translationKeys.description),
			buttonText: t(manualCard.translationKeys.button),
			imagePath: manualCard.imagePath,
			balanceDepositType: manualCard.depositType,
			digitalBalanceDisabledText: null,
			lang,
			loading: isCheckingRecentDigitalBalanceAdditions,
			disabled: isCheckingRecentDigitalBalanceAdditions,
		};
	}, [t, lang, handleCardClick, isCheckingRecentDigitalBalanceAdditions]);

	if (isLoading) {
		return <WaitingMessage />;
	}

	return (
		<Stack spacing={{ sm: 5, xs: 1 }} sx={{ flexWrap: 'wrap', minWidth: 0 }}>
			<ImagePopup
				open={showDigitalDepositBankAvailabilityAlert}
				image={DIGITAL_DEPOSIT_BANK_AVAILABILITY_ALERT_IMAGE_PATH}
				title={t('page.balance.add.digital.profile.partial_unavailability_popup.title')}
				subtitle={t('page.balance.add.digital.profile.partial_unavailability_popup.subtitle').replace(
					'{bankNames}',
					`\n${temporarilyUnavailableDigitalDepositBankNames.join(', ')}.\n`,
				)}
				buttons={[
					{
						label: t('page.balance.add.digital.profile.partial_unavailability_popup.continue_button.text'),
						variant: 'contained',
						onClick: () => {
							setShowDigitalDepositBankAvailabilityAlert(false);
							dispatch(setAddBalanceState(BalanceDepositType.Digital));
						},
					},
				]}
			/>
			<ImagePopup
				open={showManualBalanceAlert}
				image={MANUAL_BALANCE_ALERT_IMAGE_PATH}
				title={t('page.balance.add.digital.alert_popup.title')}
				subtitle={t('page.balance.add.digital.alert_popup.subtitle')}
				buttons={[
					{
						label: t('page.balance.add.digital.alert_popup.proceed_button.text'),
						variant: 'contained',
						onClick: () => {
							setShowManualBalanceAlert(false);
							dispatch(setAddBalanceState(BalanceDepositType.Manual));
						},
					},
					{
						label: t('page.balance.add.digital.alert_popup.back_button.text'),
						variant: 'outlined',
						onClick: () => setShowManualBalanceAlert(false),
					},
				]}
			/>
			<BalanceHeader />

			<Box
				style={{
					direction: lang === 'he' ? 'rtl' : 'ltr',
					textAlign: lang === 'he' ? 'right' : 'left',
				}}
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					flexDirection: { xs: 'column', md: 'row' },
					gap: { xs: 2, md: '16px' },
					width: '100%',
					mt: '12px',
				}}
			>
				<FeatureFlagsWrapper featureFlag={FeatureFlags.DigitalBalance}>
					<BalanceDashboardCard {...digitalCardProps} />
				</FeatureFlagsWrapper>

				<BalanceDashboardCard {...manualCardProps} />
			</Box>
		</Stack>
	);
}
